import React, { ReactNode, FormEvent } from 'react'
import { IntlFormatters } from 'react-intl'
import * as Yup from 'yup'
import { useFormatMessage } from '@babylon/intl'
import { Form as MedkitForm } from '@babylon/medkit'
import { VStack, Box } from '@babylon/web-platform-ui'
import Form from '../Forms/Form'
import { Field } from '@/types/partnerConfig'
import getFieldConfig, { ValidationSchema } from './getFieldConfig'
import messages from './messages'
import validationMessages from './validation.messages'
import { useConfig } from '@/redux/partnerConfig/selectors'
import { useUserExists } from '@/redux/userProfile/selectors'

type FormProps = React.ComponentProps<typeof Form>

export type OnChangeParams = { field: string; values: any }

interface Props {
  fields: Field[]
  onSubmit: FormProps['onSubmit']
  onChange?: (params: OnChangeParams) => void
  onValuesChange?: (
    previousValues?: any,
    values?: any,
    changedKeys?: string[]
  ) => void
  submitButtonText: string
  initialValues: any
  testId: string
  children?: ReactNode
  loading?: boolean
  isInterimToken?: boolean
}

const getValidationSchema = (
  fields: Field[],
  translate: IntlFormatters['formatMessage'],
  useDnaV2Design: Boolean
) => {
  const validationSchema: { [key: string]: ValidationSchema } = {}

  fields.forEach((field) => {
    const { skipValidation } = field

    if (!skipValidation) {
      const { validate } = getFieldConfig(field.type, translate, useDnaV2Design)

      if (validate) {
        if (typeof validate === 'function') {
          validationSchema[field.name] = validate(field)
        } else {
          validationSchema[field.name] = validate
        }
      }
    }
  })

  return Yup.object(validationSchema)
}

const renderField = (
  field: Field,
  translate: IntlFormatters['formatMessage'],
  useDnaV2Design: Boolean,
  userExists: Boolean,
  isInterimToken: boolean,
  pageTestId?: string
) => {
  const {
    label,
    labelKey,
    validationKey,
    helpText,
    name,
    type,
    disabled,
    required,
    props,
    skipValidation,
    Component: CustomComponent,
  } = field
  const { Component, validate, validateMessage } = getFieldConfig(
    field.type,
    translate,
    useDnaV2Design
  )

  const inputProps: React.ComponentProps<typeof Component> = props || {}
  const helpTextMessageKey = `${name}HelpText` as keyof typeof messages
  const helpTextMessage =
    messages[helpTextMessageKey] && translate(messages[helpTextMessageKey])

  const fieldHelpText = helpText || helpTextMessage

  if (useDnaV2Design) {
    inputProps.hintText = fieldHelpText
  }

  if (validateMessage) {
    inputProps.validateMessage = validateMessage
  }

  let fieldLabel

  if (!CustomComponent) {
    if (required && (skipValidation || !validate) && !validateMessage) {
      inputProps.validateMessage = translate(
        validationMessages[
          (validationKey || name) as keyof typeof validationMessages
        ]
      )
    }

    if (label) {
      fieldLabel = label
    } else {
      const translationKey = (labelKey || name) as keyof typeof messages
      fieldLabel = translate(messages[translationKey])
    }
  }

  const ComponentToUse = CustomComponent || Component

  return (
    <ComponentToUse
      key={name}
      name={name}
      label={fieldLabel}
      legend={fieldLabel} // To support Radio buttons
      helpText={fieldHelpText}
      type={type}
      disabled={
        disabled ||
        (userExists &&
          !isInterimToken &&
          pageTestId === 'registration-page-form' &&
          name !== 'phone_number' &&
          name !== 'phone_number_v2')
      }
      isDisabled={
        disabled ||
        (userExists &&
          !isInterimToken &&
          pageTestId === 'registration-page-form' &&
          name !== 'phone_number' &&
          name !== 'phone_number_v2')
      }
      required={required}
      data-testid={`field-${name}`}
      {...inputProps}
    />
  )
}

const renderFields = (
  fields: Field[],
  translate: IntlFormatters['formatMessage'],
  useDnaV2Design: Boolean,
  userExists: Boolean,
  isInterimToken: boolean,
  pageTestId?: string
) =>
  fields.map((field) =>
    renderField(
      field,
      translate,
      useDnaV2Design,
      userExists,
      isInterimToken,
      pageTestId
    )
  )

const FormRenderer = ({
  fields,
  initialValues,
  onSubmit,
  submitButtonText,
  testId,
  children,
  onChange,
  onValuesChange,
  loading,
  isInterimToken = false,
}: Props) => {
  const translate = useFormatMessage()
  const { useDnaV2Design } = useConfig()
  const userExist = useUserExists()

  const startValue: { [key: string]: string } = {}

  const emptyValues = fields.reduce((acc, field) => {
    acc[field.name] = ''

    return acc
  }, startValue)

  const validationSchema = getValidationSchema(
    fields,
    translate,
    Boolean(useDnaV2Design)
  )

  const onFormChange = (e: FormEvent<HTMLInputElement>, { values }: any) => {
    if (onChange) {
      const target = e.target as HTMLInputElement

      onChange({
        field: target.name,
        values,
      })
    }
  }

  const onFormValuesChange = (
    previousValues?: any,
    values?: any,
    changedKeys?: string[]
  ) => {
    if (onChange && changedKeys?.length) {
      changedKeys.forEach((field) => onChange({ field, values }))
    }

    if (onValuesChange) {
      onValuesChange(previousValues, values, changedKeys)
    }
  }

  return (
    <>
      {useDnaV2Design && (
        <Form
          onSubmit={onSubmit}
          submitButtonText={submitButtonText}
          validationSchema={validationSchema}
          initialValues={{
            ...emptyValues,
            ...initialValues,
          }}
          data-testid={testId}
          onChange={onFormChange}
          onValuesChange={onFormValuesChange}
          loading={loading}
          submitButtonLoadingText={translate(messages.submitButtonLoading)}
          genericFormErrorMessage={translate(
            validationMessages.genericFormError
          )}
        >
          <Box marginBottom={9}>
            <VStack spacing="5" align="flex-start">
              {renderFields(
                fields,
                translate,
                useDnaV2Design,
                userExist,
                isInterimToken,
                testId
              )}
              {children}
            </VStack>
          </Box>
        </Form>
      )}
      {!useDnaV2Design && (
        <MedkitForm
          onSubmit={onSubmit}
          submitButtonText={submitButtonText}
          validationSchema={validationSchema}
          initialValues={{
            ...emptyValues,
            ...initialValues,
          }}
          data-testid={testId}
          onChange={onFormChange}
          onValuesChange={onFormValuesChange}
          loading={loading}
          submitButtonLoadingText={translate(messages.submitButtonLoading)}
          genericFormErrorMessage={translate(
            validationMessages.genericFormError
          )}
        >
          {renderFields(
            fields,
            translate,
            false,
            userExist,
            isInterimToken,
            testId
          )}
          {children}
        </MedkitForm>
      )}
    </>
  )
}

export default FormRenderer
